import { AlongsideDay } from '@/components/plan/constellation/AlongsideDay';
// src/apps/tasks/HomeViewContainer.tsx
//
// During P4 the legacy /today path keeps using App.tsx -> ViewRouter -> HomeView
// with prop-drilled state. The new /tasks-new/today path uses this container,
// which fetches the same data via context-based hooks and renders the same
// HomeView component. The two parallel mounts share zero state during the
// parallel-path phase (P4-A) and will be unified at cutover (P4.8).
//
// P4.5 lifted meal-events synthesis to MealEventsProvider; this container now
// consumes useMealEventsForDate(viewedDate). The legacy App.tsx still has its
// own copy until full cutover (then the legacy synthesis becomes dead code).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { onPlanFromPaperRequest, consumePlanFromPaperRequest } from '@/lib/planFromPaperSignal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSupabaseTasks } from '@/hooks/useSupabaseTasks';
import { useGoogleCalendar, CalendarReconnectError } from '@/hooks/useGoogleCalendar';
import { showToast } from '@/hooks/useToast';
import { dayEventSpan, rangeEventSpan } from '@/lib/weekHelpers';
import { densitySourcesFor, type DensitySources } from '@/lib/planning/dayDensity';
import { makeEventMover } from '@/lib/calendar/moveEvent';
import { PageFromPaperFlow } from '@/components/capture/PageFromPaperFlow';
import { PaperPlanFlow } from '@/components/capture/PaperPlanFlow';
import { localYmd, readCadenceConfig, weekStartAnchor } from '@/lib/cadence/config';
import { parseRoutineTimelineId } from '@/lib/today/doseExpansion';
import { groupItems, addToGroup, removeFromGroup, ungroupTasks } from '@/lib/today/groupTasks';
import { useConvertTaskToProject } from '@/hooks/useConvertTaskToProject';
import { parseQuickInput, allDayFromParse } from '@/lib/quickInputParser';
import type { ParserContext } from '@/lib/quickInputParser';
import type { ResolverContext } from '@/lib/entityResolver';
import type { TodayCaptureResult } from '@/components/schedule/TodayAddInput';
import type { TimelineCaptureResult } from '@/components/schedule/TimelineQuickInput';
import { useEventNotes } from '@/hooks/useEventNotes';
import { eventNoteKeys } from '@/lib/today/eventFree';
import { useContacts } from '@/hooks/useContacts';
import { useProjects } from '@/hooks/useProjects';
import { useRoutines } from '@/hooks/useRoutines';
import { useActionableInstances } from '@/hooks/useActionableInstances';
import { useFamilyMembers } from '@/hooks/useFamilyMembers';
import { useHiddenCalendarEvents } from '@/hooks/useHiddenCalendarEvents';
import { useScheduleFiltering } from '@/hooks/useScheduleFiltering';
import { useInstancesRealtime } from '@/hooks/useInstancesRealtime';
import { useScheduleActions } from '@/hooks/useScheduleActions';
import { useGatedTaskActions } from '@/hooks/useGatedTaskActions';
import { useCalendarDomainMappings } from '@/hooks/useCalendarDomainMappings';
import { useRefreshOnVisible } from '@/hooks/useRefreshOnVisible';
import { useDayRollover } from '@/hooks/useDayRollover';
import { useListsContext } from '@/contexts/ListsContext';
import { useNotesContext } from '@/contexts/NotesContext';
import { ScheduleActionsProvider, type ScheduleActionsValue } from '@/contexts/ScheduleActionsContext';
import { withDefaultEventAssignees } from '@/components/home/eventAssigneeDefaults';
import { useUndo } from '@/hooks/useUndo';
import { useSendTaskToBuy } from '@/hooks/useSendTaskToBuy';
import { UndoToast } from '@/components/undo/UndoToast';
import { useResolutionLearning } from '@/hooks/useResolutionLearning';
import { HomeView } from '@/components/home';
import { useSelection } from '@/shell/providers/SelectionProvider';
import { useMealEventsForDate } from '@/shell/providers/MealEventsProvider';
import { FirstWeekCard } from '@/components/schedule/FirstWeekCard';
import { PlanningNudge } from '@/components/plan/PlanningNudge';
import { useFirstWeekSignals } from '@/hooks/useFirstWeekSignals';
import { firstWeekSteps, shouldOpenFirstWeek, paperPlanRequest, FIRST_WEEK_HIDE_KEY, hasSampleIds, readSampleIds, clearSampleIdsRecord, deleteSampleRows } from '@/lib/firstWeek';
import { getAuthUser } from '@/lib/supabase';

const sameLocalDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function HomeViewContainer({ fixedView }: { fixedView?: 'today' | 'week' } = {}) {
  // Data hooks
  const { tasks, loading: tasksLoading, error: tasksError, addTask, toggleTask, toggleWaiting, deleteTask, updateTask, updateTasksBulk, pushTask, getLinkedTasks, refetch, updateTaskOrders, userId } = useSupabaseTasks();
  // A failed read leaves `tasks` empty; that must not read as an empty day.
  // (`error` is also set by failed writes, so it only means "didn't load"
  // when nothing arrived.)
  const tasksLoadFailed = !tasksLoading && !!tasksError && tasks.length === 0;
  const retryTasks = useCallback(() => { void refetch(); }, [refetch]);
  const { isConnected, events, fetchEvents, updateEvent, createEvent, deleteEvent, removeEventLocal, restoreEventLocal, isLoading: calendarLoading, isFetching: calendarFetching, error: calendarError } = useGoogleCalendar();
  // Passing the visible event ids opts in to auto-loading notes (context
  // overrides, assignees, shared-with-family, free) + realtime — without it
  // those persist to the DB but render stale on every fresh window.
  // eventNoteKeys also loads each event's SERIES id (recurring_event_id), so
  // a "free" flag set once on the series note resolves for every occurrence.
  const visibleEventIds = useMemo(() => eventNoteKeys(events), [events]);
  const { notes: eventNotesMap, updateEventAssignment, updateEventAssignmentAll, updateEventContext, updateEventProject, updateEventSharedWithFamily, updateEventFree, dismissShareNudge } = useEventNotes(visibleEventIds);
  const { contacts, contactsMap, addContact, searchContacts } = useContacts();
  const { projects, projectsMap, addProject } = useProjects();
  const {
    routines: allRoutines,
    activeRoutines,
    getRoutinesForDate,
    loading: routinesLoading,
    updateRoutine,
    deleteRoutine,
  } = useRoutines();
  const { getInstancesForDate, markDone, undoDone, skip, reschedule, undoReschedule } = useActionableInstances();
  const { members: familyMembers, getCurrentUserMember } = useFamilyMembers();
  const { isHidden: isEventHidden, hideEvent } = useHiddenCalendarEvents();
  const { getDomainForCalendar } = useCalendarDomainMappings();
  const { lists, listsByCategory, addList } = useListsContext();
  const { addNote } = useNotesContext();
  const undo = useUndo();
  const { aliases, recordOutcome } = useResolutionLearning();

  // UI state local to this container
  const [viewedDate, setViewedDate] = useState<Date>(() => new Date());
  // Plan-from-paper (analog-planning pivot): photograph the written plan page,
  // review the parsed items, commit them as placed tasks.
  const [planFromPaperOpen, setPlanFromPaperOpen] = useState(false);
  // The sidenav asks for the flow from outside this tree (planFromPaperSignal):
  // answer while mounted, and pick up a request made before we existed.
  useEffect(() => {
    if (consumePlanFromPaperRequest()) setPlanFromPaperOpen(true);
    return onPlanFromPaperRequest(() => setPlanFromPaperOpen(true));
  }, []);
  const { selection, setSelection, clearSelection } = useSelection();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ?date=YYYY-MM-DD (from search's "jump to this task's day", and from the
  // View day link beside a timing control) sets the viewed day.
  //
  // The param used to be STRIPPED as soon as it was read, which made the day
  // survive exactly one navigation: reload, or come back from a task, and the
  // page silently returned to today with no sign the day had ever been asked
  // for (Codex review, 2026-09-24). It now stays in the URL, so reload and
  // Back land on the day you were reading — the contract `?start=` already
  // has on /week (S2-25).
  //
  // Applied on every change, not once on mount, so browser Back and Forward
  // move the day without a remount. A param equal to the day already shown is
  // ignored, which keeps this from fighting `useDayRollover`.
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (!dateParam) return;
    const [y, m, d] = dateParam.split('-').map(Number);
    if (!y || !m || !d) return;
    const asked = new Date(y, m - 1, d);
    setViewedDate((prev) => (localYmd(prev) === localYmd(asked) ? prev : asked));
  }, [searchParams]);

  /**
   * Move the viewed day, and say so in the URL — the contract `?start=`
   * already has on /week.
   *
   * It used to write the param only when one was ALREADY there, to keep a
   * clean URL. The cost showed up live: page back to Wednesday, reload, and
   * Today is Thursday again, because the day lived only in component state
   * (walkthrough, 2026-09-24). Leaving today still clears the param, so the
   * default case keeps its clean URL; any OTHER day names itself and survives
   * a reload, a Back and a link.
   */
  const changeViewedDate = useCallback((next: Date) => {
    setViewedDate(next);
    // On Week, any day of the current week is "now": none is written in, so
    // the URL never pins a week that will end (Scott, 2026-10-12).
    const weekStartsOn = readCadenceConfig().weekStartsOn;
    const isToday = localYmd(next) === localYmd(new Date())
      || (fixedView === 'week' && localYmd(weekStartAnchor(next, weekStartsOn)) === localYmd(weekStartAnchor(new Date(), weekStartsOn)));
    setSearchParams((prev) => {
      if (isToday && !prev.get('date')) return prev;
      const out = new URLSearchParams(prev);
      if (isToday) out.delete('date');
      else out.set('date', localYmd(next));
      return out;
    }, { replace: true });
  }, [setSearchParams, fixedView]);

  // "Your first week" — a fresh household's onboarding card. uid is fetched
  // once (not via useAuth, which this tree already has several copies of via
  // other hooks) purely to key the two localStorage markers (hide, sample ids).
  const [firstWeekUid, setFirstWeekUid] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void getAuthUser().then(({ data }) => { if (alive) setFirstWeekUid(data.user?.id ?? null); });
    return () => { alive = false };
  }, []);

  const { signals: firstWeekSignals, refetch: refetchFirstWeekSignals } = useFirstWeekSignals();
  const firstWeekStepsList = useMemo(() => (firstWeekSignals ? firstWeekSteps(firstWeekSignals) : []), [firstWeekSignals]);

  const [firstWeekHiddenAt, setFirstWeekHiddenAt] = useState<string | null>(null);
  useEffect(() => {
    if (!firstWeekUid) return;
    try { setFirstWeekHiddenAt(localStorage.getItem(FIRST_WEEK_HIDE_KEY(firstWeekUid))); } catch { setFirstWeekHiddenAt(null); }
  }, [firstWeekUid]);

  const [hasSample, setHasSample] = useState(false);
  useEffect(() => {
    if (!firstWeekUid) return;
    setHasSample(hasSampleIds(firstWeekUid));
  }, [firstWeekUid, firstWeekSignals]);

  // /week mounts this same container (fixedView="week") — the card is a
  // Today-only onboarding nudge, never shown on the bench.
  const showFirstWeek = fixedView !== 'week' && firstWeekUid !== null && firstWeekSignals !== null &&
    shouldOpenFirstWeek({
      asked: searchParams.get('welcome') === '1',
      taskCount: tasks.length,
      steps: firstWeekStepsList,
      hiddenAt: firstWeekHiddenAt,
      now: new Date(),
    });

  const handleHideFirstWeek = useCallback(() => {
    if (!firstWeekUid) return;
    const now = new Date().toISOString();
    try { localStorage.setItem(FIRST_WEEK_HIDE_KEY(firstWeekUid), now); } catch { /* ignore */ }
    setFirstWeekHiddenAt(now);
    if (searchParams.has('welcome')) { const next = new URLSearchParams(searchParams); next.delete('welcome'); setSearchParams(next, { replace: true }); }
  }, [firstWeekUid, searchParams, setSearchParams]);

  // "Use our sample page" hands the bundled week-page image straight to the
  // paper flow on the week altitude, skipping the camera — the same path a
  // real snap takes, so the same commit/attachment/signal machinery applies.
  const [sampleBlob, setSampleBlob] = useState<Blob | null>(null);
  const handleSamplePage = useCallback(() => {
    void fetch('/sample/week-page.jpg')
      .then((r) => r.blob())
      .then((blob) => {
        setSampleBlob(blob);
        setPlanFromPaperOpen(true);
      })
      .catch(() => showToast('Could not load the sample page', 'error'));
  }, []);

  const handleClearSample = useCallback(() => {
    if (!firstWeekUid) return;
    const ids = readSampleIds(firstWeekUid);
    void deleteSampleRows(ids).then((cleared) => {
      // The localStorage record is the ONLY handle on these rows. Clearing it
      // inside an unconditional .then() — as this used to — left the sample's
      // fake tasks and notes sitting in a real household with nothing left
      // pointing at them. On failure the record stays and the offer can be
      // taken again.
      if (!cleared) {
        showToast("Couldn't clear the sample", 'error');
        return;
      }
      clearSampleIdsRecord(firstWeekUid);
      setHasSample(false);
      void refetchFirstWeekSignals();
    });
  }, [firstWeekUid, refetchFirstWeekSignals]);

  // ?plan=paper (from the card's "Snap this week's page" link) opens the flow
  // directly on this route; ?plan=sample loads the bundled sample first.
  // Week's own ?plan=1 (planning by hand) is left for WeekV2 to read.
  useEffect(() => {
    const plan = paperPlanRequest(searchParams.get('plan'));
    if (!plan) return;
    const next = new URLSearchParams(searchParams);
    next.delete('plan');
    setSearchParams(next, { replace: true });
    if (plan === 'paper') {
      setPlanFromPaperOpen(true);
    } else if (plan === 'sample') {
      handleSamplePage();
    }
  }, [searchParams, setSearchParams, handleSamplePage]);

  // Map URL selection back to the legacy `selectedItemId` shape HomeView expects
  // (`task-<id>`, `routine-<id>`, `event-<id>`). The TimelineCard uses this to
  // highlight the selected row. Meal selections highlight as their underlying
  // `event-<meal:id>` row (HomeView emits meals as event-prefixed ids).
  const selectedItemId = useMemo(() => {
    if (!selection) return null;
    if (selection.kind === 'meal') return `event-${selection.id}`;
    if (selection.kind === 'task' || selection.kind === 'routine' || selection.kind === 'event') {
      return `${selection.kind}-${selection.id}`;
    }
    return null;
  }, [selection]);

  const handleSelectItem = useCallback(
    (itemId: string | null) => {
      if (!itemId) {
        clearSelection();
        return;
      }
      // Collection headers ("routine-collection-<uuid>") open the PARENT
      // routine's panel — the generic parse below would mangle the id.
      if (itemId.startsWith('routine-collection-')) {
        setSelection({ kind: 'routine', id: itemId.slice('routine-collection-'.length) });
        return;
      }
      // legacy itemId is "<kind>-<id>" — translate to selection ref
      const dashIdx = itemId.indexOf('-');
      if (dashIdx <= 0) return;
      const kind = itemId.slice(0, dashIdx);
      const id = itemId.slice(dashIdx + 1);
      // Meals ride on the `event-` prefix with an id of `meal:<entryId>`;
      // promote them to their own `meal` kind so the meal panel opens.
      if (kind === 'event' && id.startsWith('meal:')) {
        setSelection({ kind: 'meal', id });
        return;
      }
      // TasksApp owns task/routine/event/meal; ignore anything else.
      if (kind !== 'task' && kind !== 'routine' && kind !== 'event') return;
      // For routines, strip any dose slot so the panel looks up the routine row
      // by its real UUID. (e.g. "rx#0" → "rx"). Completion stays slotted.
      const resolvedId = kind === 'routine' ? parseRoutineTimelineId(itemId).routineId : id;
      setSelection({ kind, id: resolvedId });
    },
    [setSelection, clearSelection],
  );

  // Fetch calendar events for the viewed range. Today/Day want the viewed
  // date, but the Week bench (`/week` mounts this container with
  // fixedView="week") renders a 7-day grid from this same fetch — a
  // day-scoped fetch left every column except today empty (a Wednesday
  // appointment never reached the client at all). Week nav keeps
  // `viewedDate` inside the shown week (see HomeView's onWeekChange), so
  // the span follows the grid.
  // Which range the events we HOLD were fetched for. Paging the week leaves
  // the previous range's events on screen until the new fetch lands, and a
  // count drawn from them would describe last week (Codex, 2026-09-24).
  const [eventsRange, setEventsRange] = useState<{ start: number; end: number } | null>(null);
  // While the Week's guided session is open (?plan=1 or ?review=1, the full
  // deck), its look back shows the week before the one it plans, with that
  // week's events: the read reaches back a week further (walk 2026-11-01).
  const weekSessionOpen = fixedView === 'week' && (searchParams.get('plan') === '1' || searchParams.get('review') === '1');
  const weekEventSpan = useCallback((d: Date) => {
    const span = rangeEventSpan(d);
    if (!weekSessionOpen) return span;
    return { start: new Date(span.start.getFullYear(), span.start.getMonth(), span.start.getDate() - 7), end: span.end };
  }, [weekSessionOpen]);
  const refetchViewedDayEvents = useCallback(async () => {
    if (!isConnected) return;
    const span = fixedView === 'week'
      // Two weeks, not one: the grid may draw any run of up to seven days
      // starting in the viewed week (a weekend, a custom Thu–Wed).
      ? weekEventSpan(viewedDate)
      // The same window Home asks for, so moving between them shares a read.
      : dayEventSpan(viewedDate);
    setEventsRange(null);
    await fetchEvents(span.start, span.end);
    setEventsRange({ start: span.start.getTime(), end: span.end.getTime() });
  }, [isConnected, viewedDate, fetchEvents, fixedView, weekEventSpan]);

  /**
   * What each source can honestly say about the range in view.
   *
   * "Not connected" is not a failure and is never reported as one: there is
   * simply no calendar to read, and the count is complete without it.
   */
  const onUpdateEvent = useMemo(() => makeEventMover({
    events,
    updateEvent,
    refetch: refetchViewedDayEvents,
    notify: (message, tone) => showToast(message, tone, 5000),
  }), [events, updateEvent, refetchViewedDayEvents]);

  const densitySources = useMemo<DensitySources>(() => {
    const span = fixedView === 'week' ? weekEventSpan(viewedDate) : null;
    return densitySourcesFor({
      tasksLoading,
      routinesLoading,
      calendar: { connected: isConnected, loading: calendarLoading, fetching: calendarFetching, error: calendarError },
      heldRange: eventsRange,
      neededRange: span ? { start: span.start.getTime(), end: span.end.getTime() } : null,
    });
  }, [fixedView, viewedDate, weekEventSpan, eventsRange, tasksLoading, routinesLoading, isConnected, calendarError, calendarLoading, calendarFetching]);

  useEffect(() => {
    void refetchViewedDayEvents();
  }, [refetchViewedDayEvents]);

  // Google events have no realtime channel and nothing polls them, so the fetch
  // above was the ONLY one a tab ever did — an appointment added in Google after
  // load stayed invisible until a manual reload. Refetch on return to the tab.
  useRefreshOnVisible(refetchViewedDayEvents, { enabled: isConnected });

  // Same one-shot problem on the date itself: `viewedDate` is seeded once at
  // mount, so a tab open past midnight kept rendering yesterday.
  useDayRollover(viewedDate, setViewedDate);

  // ── Meal-plan entries synthesized as CalendarEvent objects ──
  // Sourced from <MealEventsProvider> mounted in Shell. Legacy App.tsx still
  // has its own copy of this synthesis; that becomes dead code post-cutover.
  const mealEvents = useMealEventsForDate(viewedDate);
  const eventsWithMeals = useMemo(() => [...events, ...mealEvents], [events, mealEvents]);

  // Plan-from-paper's day-fact check ("dentist 10am" already on the calendar)
  // needs every synced event's title, by day — not just the viewed date.
  const calendarTitlesByDay = useMemo(() => {
    const byDay = new Map<string, string[]>();
    for (const ev of events) {
      const raw = ev.start_time || ev.startTime;
      if (!raw) continue;
      const day = localYmd(new Date(raw));
      const titles = byDay.get(day);
      if (titles) titles.push(ev.title);
      else byDay.set(day, [ev.title]);
    }
    return byDay;
  }, [events]);

  // Synced calendar events carry no Symphony assignee, and the schedule views
  // (Today/Day/Week/Month) hide events whose assignee isn't selected — so a
  // freshly-synced event vanishes for everyone until manually assigned. Each
  // member only syncs their OWN Google calendars, so default an unassigned
  // event's assignee to the current member. Feeding this into the shared
  // ScheduleActions context covers every view at once. See eventAssigneeDefaults.
  const eventNotesMapWithDefaults = useMemo(
    () => withDefaultEventAssignees(eventNotesMap, events, getCurrentUserMember()?.id),
    [eventNotesMap, events, getCurrentUserMember],
  );

  // Schedule filtering (events/routines/instances narrowed to the VIEWED DATE
  // only — domain scoping is each consumer's job)
  const { filteredEvents, filteredRoutines, dateInstances, refreshDateInstances } = useScheduleFiltering({
    viewedDate,
    events: eventsWithMeals,
    allRoutines,
    getRoutinesForDate,
    getInstancesForDate,
    isEventHidden,
    tasksLoading,
    routinesLoading,
    getLinkedTasks,
    addTask,
    getCurrentUserMember,
  });

  // Completions land in actionable_instances from outside this tree too — the
  // event detail panel (mounts at the Shell level), other windows, the wall,
  // iOS. Refresh the day's instances whenever any of them writes.
  useInstancesRealtime(refreshDateInstances);

  // Schedule action handlers
  const scheduleActions = useScheduleActions({
    tasks,
    events,
    allRoutines,
    familyMembers,
    viewedDate,
    updateTask,
    updateRoutine,
    deleteRoutine,
    updateEventAssignment,
    updateEventAssignmentAll,
    markDone,
    undoDone,
    skip,
    reschedule,
    undoReschedule,
    refreshDateInstances,
    pushAction: undo.pushAction,
  });

  const eventContextOverrides = useMemo(() => {
    const overrides = new Map<string, import('@/types/task').TaskContext>();
    for (const [eventId, note] of eventNotesMap) {
      if (note.context) overrides.set(eventId, note.context);
    }
    return overrides;
  }, [eventNotesMap]);

  const onCreateTaskFromValue = useCallback(
    async (raw: string) => {
      // Natural-language parse the quick-add text: pull out date/time, project,
      // contact, assignees, and category, leaving a clean title. e.g.
      // "call macmillan guitars at 930am" → title "call macmillan guitars",
      // scheduled today 9:30am. An explicit time/date wins over the default
      // "today, all-day"; with no date we keep the original add-to-today behavior.
      const parsed = parseQuickInput(raw, { projects, contacts, familyMembers });
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const scheduledFor = parsed.dueDate ?? today;
      await addTask(parsed.title || raw, parsed.contactId, parsed.projectId, scheduledFor, {
        assignedTo: parsed.assignedMemberIds?.[0] ?? getCurrentUserMember()?.id,
        assignedToAll: parsed.assignedMemberIds,
        context: undefined,
        category: parsed.category,
        // A parsed CLOCK TIME means a specific time-of-day → not all-day. A
        // bare date does not: the parser zeroes the time for a date-only
        // match, so the old `parsed.dueDate ? false : true` filed "for Monday"
        // as a timed block at 12:00 AM. No date at all → our default day
        // (today), all-day.
        isAllDay: allDayFromParse(parsed) ?? true,
        // Added from Today with no other day named: chosen for today, so it
        // lands on the main list rather than waiting in the pin.
        plannedOn: sameLocalDay(scheduledFor, today) ? today : undefined,
      });
    },
    [addTask, getCurrentUserMember, projects, contacts, familyMembers],
  );

  const parserContext = useMemo<ParserContext>(
    () => ({ projects, contacts, familyMembers }),
    [projects, contacts, familyMembers],
  );

  const resolverContext = useMemo<ResolverContext>(
    () => ({
      contacts: contacts.map((c) => ({ id: c.id, name: c.name, phone: c.phone ?? undefined })),
      aliases,
    }),
    [contacts, aliases],
  );

  const getRecentTaskForContact = useCallback(
    (contactId: string) => {
      const recent = tasks
        .filter((t) => t.contactId === contactId)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];
      return recent ? { title: recent.title, date: recent.createdAt } : null;
    },
    [tasks],
  );

  const onCreateTaskParsed = useCallback(
    async (r: TodayCaptureResult) => {
      // Destination routing: one visible input handles all captures.
      if (r.destination === 'note') {
        const note = await addNote({
          content: r.title,
          // Only from an explicit #work/#family/#personal token; never the lens.
          context: r.context,
        });
        showToast(note ? 'Note saved' : 'Could not save the note', note ? 'success' : 'error');
        return !!note;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const toInbox = r.destination === 'inbox' && !r.scheduledFor;
      const taskId = await addTask(
        r.title,
        r.contactId,
        r.projectId,
        // Inbox destination = unscheduled; an explicit parsed date always wins.
        toInbox ? undefined : (r.scheduledFor ?? today),
        {
          assignedTo: r.assignedMemberIds?.[0] ?? getCurrentUserMember()?.id,
          assignedToAll: r.assignedMemberIds,
          // Only from an explicit #work/#family/#personal token; never the lens.
          context: r.context,
          category: r.category,
          // A parsed date with no certain time (e.g. "for Monday") is all-day
          // even though a time is present; r.isAllDay carries that when set.
          isAllDay: toInbox ? undefined : r.isAllDay ?? (r.scheduledFor ? false : true),
          phoneNumber: r.phoneNumber,
          // Typed into Today for today: a choice, so it is on the main list.
          // A named other day ("for Monday") is only a date until chosen.
          plannedOn: !toInbox && (!r.scheduledFor || sameLocalDay(r.scheduledFor, today)) ? today : undefined,
        },
      );
      if (toInbox && taskId) showToast('Added to Inbox', 'success');
      if (r.resolution) {
        recordOutcome({
          inputText: r.resolution.inputText,
          suggestion: r.resolution.suggestion,
          action: r.resolution.action,
          taskId,
        });
      }
      // False tells the capture box to give the words back for a retry.
      return !!taskId;
    },
    [addTask, addNote, getCurrentUserMember, recordOutcome],
  );

  // Inline timeline "+" create: the TimelineInsertPoint quick-input captures a
  // title + anchor time. Create a real task at that time (addTask buckets it as
  // 'timed' whenever scheduledFor is set, so it lands on the timeline rather
  // than vanishing). Without this handler wired through context the inline add
  // was a silent no-op.
  // Needed Today's "schedule a buy item": the purchase stays on its list; a
  // timed task linked to it carries it into the day's agenda. The link makes
  // completing the task check the item off (see useSupabaseTasks.toggleTask).
  const onScheduleListItemAsTask = useCallback(
    async (item: { id: string; title: string }, date: Date, isAllDay: boolean) => {
      const id = await addTask(item.title, undefined, undefined, date, {
        assignedTo: getCurrentUserMember()?.id,
        isAllDay,
        linkedTo: { type: 'list_item', id: item.id },
      });
      // addTask swallows insert failures (rolls back and resolves undefined) —
      // a caller that clears state on resolve would eat the mark on a failed
      // spawn. That EXACTLY happened: the tasks_linked_activity_type_check
      // constraint predated 'list_item', every insert bounced, and the note
      // still dropped the row. Throw so the note keeps the mark, and say why.
      if (!id) {
        showToast("Couldn't schedule that item", 'error', 4000);
        throw new Error('list-item task spawn failed');
      }
    },
    [addTask, getCurrentUserMember],
  );

  const onCreateTaskAt = useCallback(
    async (r: TimelineCaptureResult) => {
      await addTask(r.title, r.contactId, r.projectId, r.scheduledFor ?? undefined, {
        assignedTo: getCurrentUserMember()?.id,
        assignedToAll: r.assignedMemberIds,
        // Only from an explicit #work/#family/#personal token; never the lens.
        context: r.context,
        // A timed anchor means a specific time-of-day, so it is not all-day.
        isAllDay: r.scheduledFor ? false : undefined,
      });
    },
    [addTask, getCurrentUserMember],
  );

  // Inline timeline "+" create for the EVENT kind: make a real calendar event
  // at the captured anchor time (default 1h duration), then refetch the day so
  // it shows immediately. Without this the inline event create was a no-op.
  const onCreateEventAt = useCallback(
    async (r: TimelineCaptureResult) => {
      const start = r.scheduledFor ?? viewedDate;
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      await createEvent({
        title: r.title,
        startTime: start,
        endTime: end,
        allDay: !r.scheduledFor,
      });
      // Refresh the viewed day's events so the new one appears right away.
      await refetchViewedDayEvents();
    },
    [createEvent, refetchViewedDayEvents, viewedDate],
  );

  // Inline timeline "+" create for the ROUTINE kind: a routine needs a
  // recurrence pattern that doesn't fit a one-line input, so (mirroring
  // WeekViewV2) build a natural-language string from the title + anchor and
  // hand off to the routine builder, which parses recurrence.
  const onCreateRoutineAt = useCallback(
    (r: TimelineCaptureResult) => {
      const anchor = r.scheduledFor ?? viewedDate;
      const weekday = anchor.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const timeStr = anchor
        .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        .toLowerCase()
        .replace(/\s/g, '');
      const initialNl = `${r.title} every ${weekday} at ${timeStr}`;
      navigate(`/routines/new?initial=${encodeURIComponent(initialNl)}`);
    },
    [navigate, viewedDate],
  );

  const handleCreateFollowUp = useCallback(
    async (title: string, sourceTaskId: string) => {
      const sourceTask = tasks.find(t => t.id === sourceTaskId);
      if (!sourceTask) return;
      const fullTitle = `${sourceTask.title}: ${title}`;
      await addTask(
        fullTitle,
        sourceTask.contactId,
        sourceTask.projectId,
        viewedDate,
        {
          assignedTo: sourceTask.assignedTo ?? getCurrentUserMember()?.id,
          context: sourceTask.context,
          category: sourceTask.category,
          parentTaskId: sourceTask.id,
        },
      );
    },
    [tasks, addTask, viewedDate, getCurrentUserMember],
  );

  // Bulk-select grouping (Today): wrap a mix of tasks/events/routines into a new
  // wrapper task. Tasks reparent via parentTaskId; events/routines ride as
  // group_members refs (grouping.ts relocates them under the wrapper card).
  const handleGroupItems = useCallback(
    async (
      taskIds: string[],
      memberRefs: import('@/types/task').GroupMemberRef[],
      groupName: string,
      date: Date,
      isAllDay: boolean,
    ) => {
      return await groupItems({ taskIds, memberRefs, groupName, date, isAllDay }, { addTask, updateTask, refetch });
    },
    [addTask, updateTask, refetch],
  );

  // Add members to a group that already exists (Today's drag-onto-a-group).
  // `existingMemberRefs` is read HERE, from the live task list, rather than
  // threaded down from the drag layer — addToGroup cannot defend itself against
  // a stale array, and a stale one silently drops every member it doesn't see
  // (Stage 2a residual 4).
  const handleAddToGroup = useCallback(
    async (
      wrapperId: string,
      taskIds: string[],
      memberRefs: import('@/types/task').GroupMemberRef[],
      date: Date,
      isAllDay: boolean,
    ) => {
      const wrapper = tasks.find((t) => t.id === wrapperId);
      await addToGroup(
        {
          wrapperId,
          taskIds,
          memberRefs,
          existingMemberRefs: wrapper?.groupMembers ?? [],
          date,
          isAllDay,
        },
        { addTask, updateTask, refetch },
      );
    },
    [tasks, addTask, updateTask, refetch],
  );

  const handleRemoveFromGroup = useCallback(
    async (taskId: string) => {
      await removeFromGroup(taskId, { updateTask, refetch });
    },
    [updateTask, refetch],
  );

  // Undo of "make a group": detach every child, then delete the wrapper, so
  // the two cards return to being loose rows.
  const handleUngroup = useCallback(
    async (wrapperId: string, childIds: string[]) => {
      await ungroupTasks(wrapperId, childIds, { updateTask, deleteTask, refetch });
    },
    [updateTask, deleteTask, refetch],
  );

  // Optimistic event delete: drop from the local cache immediately, restore
  // on failure. Google keeps deleted events in calendar trash (~30 days), so
  // a confirmed delete is still recoverable from the Calendar web UI.
  const handleDeleteEvent = useCallback(
    (event: import('@/hooks/useGoogleCalendar').CalendarEvent) => {
      // google_event_id is the reliable key: events from the edge function
      // carry no db `id` (undefined), and removing by undefined wiped the
      // whole local cache (2026-06-12 incident).
      const eventId = event.google_event_id ?? event.id;
      if (!eventId) return;
      // calendar_id (snake_case) comes from the events edge function, but some
      // runtime paths surface it camelCased as calendarId. Without this fallback
      // the delete defaults to 'primary' and Google rejects it (404) for any
      // event on a secondary calendar — the .catch then restores it, so the
      // event "pops right back up". (The legacy App.tsx handler has this fallback;
      // the Shell rewrite dropped it.)
      const calendarId = event.calendar_id ?? event.calendarId;
      removeEventLocal(eventId);
      deleteEvent({ eventId, calendarId }).catch((err) => {
        // Surface the failure — silently restoring looks like the event came
        // back for no reason. showToast is a stable module-level singleton.
        if (err instanceof CalendarReconnectError) {
          showToast('Calendar connection expired. Please reconnect.', 'warning');
        } else {
          console.error('Failed to delete event:', err);
          showToast(err instanceof Error ? err.message : 'Failed to delete event', 'warning');
        }
        restoreEventLocal(event);
      });
    },
    [deleteEvent, removeEventLocal, restoreEventLocal],
  );

  // Expand a task into a new project (subtasks absorbed, parent task deleted).
  const handleConvertTaskToProject = useConvertTaskToProject(tasks, { addProject, updateTask, deleteTask });

  // ── "To buy" conversion — task → list item, with undo ──────────────────────
  // Shared with InboxViewContainer (see useSendTaskToBuy); both surfaces render
  // the ToBuyNudge and both must be able to act on it.
  const sendTaskToBuy = useSendTaskToBuy({ tasks, lists, addList, deleteTask, addTask });

  const onSetNeededToday = useCallback(
    (taskId: string, neededOn: Date | null) => {
      void updateTask(taskId, { neededOn: neededOn ?? undefined });
    },
    [updateTask],
  );

  // Iris's rule: any process on an Unsorted item has to involve giving it a
  // domain. These five actions are the processes (see useGatedTaskActions);
  // everything else goes to the raw hook handlers unchanged. `raw` is memoized
  // on its own stable (useCallback-wrapped) members so `gated` — and therefore
  // scheduleActionsValue below — keeps one identity across renders instead of
  // forcing every ScheduleActions consumer to re-render every time.
  const findTaskById = useCallback((id: string) => tasks.find((t) => t.id === id), [tasks]);
  const gatedRaw = useMemo(
    () => ({
      updateTask,
      pushTask,
      updateTasksBulk,
      onAssignTask: scheduleActions.onAssignTask,
      onAssignTaskAll: scheduleActions.onAssignTaskAll,
    }),
    [updateTask, pushTask, updateTasksBulk, scheduleActions.onAssignTask, scheduleActions.onAssignTaskAll],
  );
  const gated = useGatedTaskActions(gatedRaw, findTaskById);

  const scheduleActionsValue = useMemo<ScheduleActionsValue>(
    () => ({
      // Task actions
      onToggleTask: toggleTask,
      onToggleWaiting: toggleWaiting,
      onUpdateTask: gated.updateTask,
      onUpdateTasksBulk: gated.updateTasksBulk,
      onPushTask: gated.pushTask,
      onDeleteTask: deleteTask,
      onSetNeededToday,
      viewedDate,
      onCreateTask: onCreateTaskFromValue,
      onCreateTaskParsed,
      parserContext,
      resolverContext,
      getRecentTaskForContact,
      onScheduleListItemAsTask,
      onCreateTaskAt,
      onCreateEventAt,
      onCreateRoutineAt,
      onCreateFollowUp: handleCreateFollowUp,
      onGroupItems: handleGroupItems,
      onAddToGroup: handleAddToGroup,
      onRemoveFromGroup: handleRemoveFromGroup,
      onRegisterUndo: undo.pushAction,
      onUngroup: handleUngroup,
      onReorderTasks: updateTaskOrders,
      onOpenTask: (taskId: string) => setSelection({ kind: 'task', id: taskId }),
      onSendTaskToBuy: sendTaskToBuy,
      onOpenProject: (projectId: string) => navigate(`/projects/${projectId}`),

      // Assignment actions
      onAssignTask: gated.onAssignTask,
      onAssignTaskAll: gated.onAssignTaskAll,
      onAssignEvent: scheduleActions.onAssignEvent,
      onAssignEventAll: scheduleActions.onAssignEventAll,
      onAssignRoutine: scheduleActions.onAssignRoutine,
      onAssignRoutineAll: scheduleActions.onAssignRoutineAll,

      // Routine actions
      onCompleteRoutine: scheduleActions.onCompleteRoutine,
      onSkipRoutine: scheduleActions.onSkipRoutine,
      onPushRoutine: scheduleActions.onPushRoutine,
      onMoveRoutineToDay: scheduleActions.onMoveRoutineToDay,
      onDeleteRoutine: scheduleActions.onDeleteRoutine,
      onUpdateRoutine: updateRoutine,

      // Event actions
      onCompleteEvent: scheduleActions.onCompleteEvent,
      onSkipEvent: scheduleActions.onSkipEvent,
      onPushEvent: scheduleActions.onPushEvent,
      // Moving an event to another time. Until now nothing supplied this and
      // HomeView fell back to a no-op, so a week drag announced a move it had
      // not made (Scott, 2026-09-24).
      onUpdateEvent,
      // /week's quick-create refetches through this, the same range the grid
      // draws from — createEvent alone never updates the held events.
      onRefetchEvents: refetchViewedDayEvents,
      onUpdateEventContext: updateEventContext,
      onUpdateEventFree: updateEventFree,
      onShareEventWithFamily: (id: string) => updateEventSharedWithFamily(id, true),
      onDismissShareNudge: (id: string) => dismissShareNudge(id),
      onHideEvent: hideEvent,
      onDeleteEvent: handleDeleteEvent,

      // Reference data
      contactsMap,
      projectsMap,
      projects,
      contacts,
      familyMembers,
      lists,
      listsByCategory,
      eventNotesMap: eventNotesMapWithDefaults,
      eventContextOverrides,

      // List/contact actions
      onAddProject: addProject,
      onConvertTaskToProject: handleConvertTaskToProject,
      onSearchContacts: searchContacts,
      onAddContact: (name, details) => addContact({ name, ...details }),

      // Calendar domain mapping
      getDomainForCalendar,

      // Navigation
      onRefreshInstances: refreshDateInstances,
      onUpdateEventProject: updateEventProject,
    }),
    [
      toggleTask, toggleWaiting, gated, deleteTask, onSetNeededToday, viewedDate, onCreateTaskFromValue, onCreateTaskParsed, parserContext, resolverContext, getRecentTaskForContact, onScheduleListItemAsTask, onCreateTaskAt, onCreateEventAt, onCreateRoutineAt, handleCreateFollowUp, handleGroupItems, handleAddToGroup, handleRemoveFromGroup, handleUngroup, undo.pushAction, updateTaskOrders,
      setSelection, navigate,
      scheduleActions, updateRoutine, updateEventContext, updateEventFree, updateEventSharedWithFamily, dismissShareNudge, hideEvent, handleDeleteEvent, sendTaskToBuy,
      contactsMap, projectsMap, projects, contacts, familyMembers, lists, listsByCategory,
      eventNotesMapWithDefaults, eventContextOverrides,
      addProject, handleConvertTaskToProject, searchContacts, addContact, getDomainForCalendar,
      refreshDateInstances, updateEventProject, refetchViewedDayEvents,
    ],
  );

  return (
    <ScheduleActionsProvider value={scheduleActionsValue}>
      {fixedView !== 'week' && firstWeekUid && showFirstWeek && (
        // The SAME column the Today masthead draws in (TodayView's
        // max-w-[1152px] + md:px-10 lg:px-14), so the card sits directly above
        // the day card on the same left and right edges. Without it the card
        // spanned the full content width and hung out past the page.
        <div className="w-full max-w-[1152px] mr-auto px-0 pt-2 md:px-10 md:pt-8 lg:px-14">
          <FirstWeekCard
            steps={firstWeekStepsList}
            onHide={handleHideFirstWeek}
            onSamplePage={handleSamplePage}
            onClearSample={hasSample ? handleClearSample : undefined}
          />
        </div>
      )}

      {/* Planning or reviewing the day (Today ?plan=1 / ?review=1) is its own
          screen: the week column beside it would list the same things twice,
          so it steps aside. The workspace shell stays: swapping it for a bare
          fragment remounted the day page, which lost its way back on Leave
          (walk, 2026-11-01). */}
      <AlongsideDayBridge onSelect={id=>handleSelectItem(`task-${id}`)} enabled={searchParams.get('view') === 'alongside'} stepAside={fixedView !== 'week' && (searchParams.get('plan') === '1' || searchParams.get('review') === '1')} tasks={tasks} date={viewedDate} update={gated.updateTask} complete={toggleTask} loading={tasksLoading} error={tasksLoadFailed} retry={retryTasks} weekView={fixedView === 'week'}>
      <HomeView
        registerUndo={undo.pushAction}
        // The week reminder is Today's, below its schedule (2026-09-22): a
        // quiet line, never a banner above the date. Renders null when there
        // is nothing to say.
        todayAfterSchedule={fixedView !== 'week' && firstWeekUid ? <PlanningNudge uid={firstWeekUid} only="week" /> : undefined}
        tasks={tasks}
        userId={userId}
        events={filteredEvents}
        densitySources={densitySources}
        routines={filteredRoutines}
        allActiveRoutines={activeRoutines}
        projects={projects}
        dateInstances={dateInstances}
        selectedItemId={selectedItemId}
        onSelectItem={handleSelectItem}
        // The calendar is NOT allowed to gate the day.
        //
        // Google is the one source here that can fail indefinitely — an
        // expired session, a revoked token, an edge function 404 — and while
        // `eventsFetching` was in this expression a calendar that never
        // answered left Today showing "Loading…" over tasks and routines that
        // had arrived perfectly well (2026-09-04, prod). Events fill in when
        // they arrive; the rest of the day should never wait on them.
        loading={tasksLoading || routinesLoading}
        tasksLoadFailed={tasksLoadFailed}
        onRetryTasks={retryTasks}
        viewedDate={viewedDate}
        onDateChange={changeViewedDate}
        fixedView={fixedView}
      />
      </AlongsideDayBridge>

      {/* Plan from paper: a real page goes through the conversational review
          (PaperPlanFlow); the bundled sample keeps the quick week-page sheet. */}
      {planFromPaperOpen && !sampleBlob && (
        <PaperPlanFlow members={familyMembers} onClose={() => setPlanFromPaperOpen(false)} />
      )}
      {planFromPaperOpen && sampleBlob && (
        <PageFromPaperFlow
          members={familyMembers}
          onClose={() => {
            setPlanFromPaperOpen(false);
            setSampleBlob(null);
          }}
          existingTasks={tasks.filter((t) => !t.completed).map((t) => ({ id: t.id, title: t.title }))}
          calendarTitlesByDay={calendarTitlesByDay}
          initialBlob={sampleBlob ?? undefined}
          initialAltitude={sampleBlob ? 'week' : undefined}
          sample={!!sampleBlob}
        />
      )}


      {/* Every action here already registered an undo — this container just
          never rendered the toast, so skipping a routine on Today silently
          offered no way back. Reported after an accidental skip. */}
      <UndoToast
        action={undo.currentAction}
        onUndo={undo.executeUndo}
        onDismiss={undo.dismiss}
      />
    </ScheduleActionsProvider>
  );
}

function AlongsideDayBridge({enabled,...props}: React.ComponentProps<typeof AlongsideDay> & {enabled:boolean}) { return enabled ? <AlongsideDay {...props}/> : <>{props.children}</> }
